import classNames from 'classnames';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { ModalBodyScroll } from 'src/components/common/modal-body-scroll/ModalBodyScroll';
import { ASX_USER_LOCALE } from 'src/constants';
import { useDropdownContext } from 'src/contexts/DropdownContext';
import { THEME } from 'src/enums/theme';
import useDeviceSize from 'src/hooks/useDeviceSize';
import CheckIcon from 'src/icons/check.svg';
import ChevronDownIcon from 'src/icons/chevron-down.svg';
import { emitCountMetric } from 'src/logger';
import { LOCALE_PICKER_COUNT } from 'src/logging-helpers/metrics-constants';

import styles from './LocalePicker.module.scss';

const DEFAULT_LOCALE = 'en-US';
const MONS_LOCALE_QUERY_PARAM = '?mons_sel_locale=';

type LocaleLabels = {
  short: string;
  full: string;
  mobileLabel: string;
};

const LABELS_BY_LOCALE: Record<string, LocaleLabels> = {
  'ar-AE': {
    short: '🇦🇪 AR',
    full: '🇦🇪 العربية (AE)',
    mobileLabel: 'العربية (AE)',
  },
  'bn-IN': {
    short: '🇮🇳 BN',
    full: '🇮🇳 বাংলা (IN)',
    mobileLabel: 'বাংলা (IN)',
  },
  'cs-CZ': {
    short: '🇨🇿 CS',
    full: '🇨🇿 Čeština (CZ)',
    mobileLabel: 'Čeština (CZ)',
  },
  'de-DE': {
    short: '🇩🇪 DE',
    full: '🇩🇪 Deutsch (DE)',
    mobileLabel: 'Deutsch (DE)',
  },
  'en-US': {
    short: '🇺🇸 EN',
    full: '🇺🇸 English (US)',
    mobileLabel: 'English (US)',
  },
  'es-ES': {
    short: '🇪🇸 ES',
    full: '🇪🇸 Español (ES)',
    mobileLabel: 'Español (ES)',
  },
  'es-MX': {
    short: '🇲🇽 ES',
    full: '🇲🇽 Español (MX)',
    mobileLabel: 'Español (MX)',
  },
  'fr-FR': {
    short: '🇫🇷 FR',
    full: '🇫🇷 Français (FR)',
    mobileLabel: 'Français (FR)',
  },
  'hi-IN': {
    short: '🇮🇳 HI',
    full: '🇮🇳 हिन्दी (IN)',
    mobileLabel: 'हिन्दी (IN)',
  },
  'it-IT': {
    short: '🇮🇹 IT',
    full: '🇮🇹 Italiano (IT)',
    mobileLabel: 'Italiano (IT)',
  },
  'ja-JP': {
    short: '🇯🇵 JA',
    full: '🇯🇵 日本語 (日本)',
    mobileLabel: '日本語 (日本)',
  },
  'km-KH': {
    short: '🇰🇭 KM',
    full: '🇰🇭 ខ្មែរ (KH)',
    mobileLabel: 'ខ្មែរ (KH)',
  },
  'kn-IN': {
    short: '🇮🇳 KN',
    full: '🇮🇳 ಕನ್ನಡ (IN)',
    mobileLabel: 'ಕನ್ನಡ (IN)',
  },
  'mr-IN': {
    short: '🇮🇳 MR',
    full: '🇮🇳 मराठी (IN)',
    mobileLabel: 'मराठी (IN)',
  },
  'pl-PL': {
    short: '🇵🇱 PL',
    full: '🇵🇱 Polski (PL)',
    mobileLabel: 'Polski (PL)',
  },
  'pt-BR': {
    short: '🇧🇷 PT',
    full: '🇧🇷 Português (BR)',
    mobileLabel: 'Português (BR)',
  },
  'pt-PT': {
    short: '🇵🇹 PT',
    full: '🇵🇹 Português (PT)',
    mobileLabel: 'Português (PT)',
  },
  'si-LK': {
    short: '🇱🇰 SI',
    full: '🇱🇰 සිංහල (LK)',
    mobileLabel: 'සිංහල (LK)',
  },
  'sk-SK': {
    short: '🇸🇰 SK',
    full: '🇸🇰 Slovenčina (SK)',
    mobileLabel: 'Slovenčina (SK)',
  },
  'ta-IN': {
    short: '🇮🇳 TA',
    full: '🇮🇳 தமிழ் (IN)',
    mobileLabel: 'தமிழ் (IN)',
  },
  'te-IN': {
    short: '🇮🇳 TE',
    full: '🇮🇳 తెలుగు (IN)',
    mobileLabel: 'తెలుగు (IN)',
  },
  'tr-TR': {
    short: '🇹🇷 TR',
    full: '🇹🇷 Türkçe (TR)',
    mobileLabel: 'Türkçe (TR)',
  },
  'vi-VN': {
    short: '🇻🇳 VI',
    full: '🇻🇳 Tiếng Việt (VN)',
    mobileLabel: 'Tiếng Việt (VN)',
  },
  'zh-CN': {
    short: '🇨🇳 ZH',
    full: '🇨🇳 普通话 (中国)',
    mobileLabel: '普通话 (中国)',
  },
  'zh-TW': {
    short: '🇹🇼 ZH',
    full: '🇹🇼 中文 (繁體)',
    mobileLabel: '中文 (繁體)',
  },
};

interface LocaleOptionProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Component for individual locale option row in dropdown.
 */
const LocaleOption: FunctionComponent<LocaleOptionProps> = ({ label, isSelected, onClick }) => (
  <div
    className={classNames(styles.dropdownOption, {
      [styles.selected]: isSelected,
    })}
    onClick={onClick}
    onKeyDown={(event) => event.key === 'Enter' && onClick()}
    role="option"
    aria-selected={isSelected}
    tabIndex={0}
  >
    {label}

    {isSelected && (
      <span className={styles.checkContainer}>
        <CheckIcon className={styles.checkIcon} />
      </span>
    )}
  </div>
);

type LocalePickerVariant = 'primary' | 'secondary';

interface LocalePickerProps {
  /** Variant describes the style of the locale picker when the sub-nav or mobile-nav menus are expanded. "primary" variant for collapsed and "secondary" for expanded. */
  variant: LocalePickerVariant;
  /** Theme describes the style of the locale picker against the main theme of the navbar. */
  theme?: THEME;
  ariaLabel?: string;
  selectLanguageCopy: string;
}

/**
 * Component for locale picker button and dropdown.
 */
export const LocalePicker: FunctionComponent<LocalePickerProps> = ({
  variant = 'primary',
  theme = THEME.LIGHT,
  ariaLabel,
  selectLanguageCopy,
}) => {
  const { isSmallDesktop } = useDeviceSize();
  const { isDropdownOpen, toggleDropdown, closeDropdown, registerDropdownRef } = useDropdownContext();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [isScrollEnd, setIsScrollEnd] = useState(false);
  const [currLocale, setCurrLocale] = useState(localStorage.getItem(ASX_USER_LOCALE) ?? DEFAULT_LOCALE);

  const isExpanded = isDropdownOpen('locale-picker');

  const currentLocaleLabel = LABELS_BY_LOCALE[currLocale] ?? LABELS_BY_LOCALE[DEFAULT_LOCALE];

  const localeDisplayLabel = isSmallDesktop ? currentLocaleLabel.short : currentLocaleLabel.mobileLabel;

  // Only apply the secondary (expanded) variant if the navbar is not dark themed.
  // When dark themed, the collapsed and expanded variants are the same style.
  const isSecondary = variant === 'secondary' && theme !== THEME.DARK;

  // Register the dropdown ref for outside click detection
  useEffect(() => {
    registerDropdownRef('locale-picker', dropdownRef.current);

    return () => registerDropdownRef('locale-picker', null);
  }, [registerDropdownRef]);

  useEffect(() => {
    if (!isSmallDesktop && isExpanded) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isExpanded, isSmallDesktop]);

  /**
   * On locale change, set localStorage value on browser and redirect with Mons Selection API
   * https://w.amazon.com/bin/view/Mons/Selections/#HMonsLanguageAPI
   */
  const handleChange = (localeCode: string) => {
    setCurrLocale(localeCode);
    localStorage.setItem(ASX_USER_LOCALE, localeCode);
    closeDropdown();

    // Emit EMF metric to determine which locale users choose
    emitCountMetric(LOCALE_PICKER_COUNT, 1, {
      Locale: localeCode,
    });

    // Convert locale string to Mons format and invoke redirect URL to set cookie on future requests.
    const monsLocale = localeCode.replace('-', '_');

    window.location.assign(`${window.location.pathname}${MONS_LOCALE_QUERY_PARAM}${monsLocale}`);
  };

  const desktopDropdown = (
    <div
      className={classNames(styles.dropdown, styles.show, styles[theme])}
      onMouseEnter={() => {}}
      onMouseLeave={closeDropdown}
    >
      {Object.entries(LABELS_BY_LOCALE).map(([localeCode, labels]) => (
        <LocaleOption
          key={localeCode}
          label={labels.full}
          isSelected={currLocale === localeCode}
          onClick={() => handleChange(localeCode)}
        />
      ))}
    </div>
  );

  const mobileDropdown = (
    <dialog
      ref={dialogRef}
      className={classNames(styles.mobileDialog, styles[theme])}
      aria-label={selectLanguageCopy}
      onClose={closeDropdown}
    >
      <ModalBodyScroll
        className={styles.bodyScroll}
        onScrollEnd={
          // istanbul ignore next
          () => {
            setIsScrollEnd(true);
          }
        }
        onScrollAway={
          // istanbul ignore next
          () => {
            setIsScrollEnd(false);
          }
        }
      >
        <div className={styles.mobileDialogHeader}>
          <span>{selectLanguageCopy}</span>

          <button type="button" onClick={closeDropdown} className={styles.closeButton} aria-label="Close locale picker">
            ✕
          </button>
        </div>

        <div className={styles.mobileDialogContent}>
          {Object.entries(LABELS_BY_LOCALE).map(([localeCode, labels]) => (
            <LocaleOption
              key={localeCode}
              label={labels.full}
              isSelected={currLocale === localeCode}
              onClick={() => handleChange(localeCode)}
            />
          ))}
        </div>
      </ModalBodyScroll>

      <div
        className={classNames(styles.gradient, {
          [styles.scrollEnd]: isScrollEnd,
        })}
      />
    </dialog>
  );

  const localePickerButton = (
    <div
      role="menu"
      tabIndex={0}
      aria-label={ariaLabel}
      className={classNames(styles.localePicker, styles[theme], {
        [styles.secondary]: isSecondary,
      })}
      onClick={() => {
        toggleDropdown('locale-picker');
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          toggleDropdown('locale-picker');
        }
      }}
    >
      <span className={styles.localeLabel}>{localeDisplayLabel}</span>

      <span>
        <ChevronDownIcon
          className={classNames(styles.localePickerIcon, {
            [styles.rotated]: isExpanded,
          })}
        />
      </span>
    </div>
  );

  return (
    <div ref={dropdownRef}>
      {localePickerButton}
      {isExpanded && (isSmallDesktop ? desktopDropdown : mobileDropdown)}
    </div>
  );
};
